import { AnyAction } from "redux";
import {
  ChangeMeta,
  JSONPatchDocument,
  ModelWithMeta,
  PagedResponse,
  PatchWithMeta,
  QueryParams,
  ValidationError,
} from "@ofqual/portal-core/core/types";
import { Organisation } from "../domain/Organisation";
import {
  LOADED_ORGANISATION_LIST,
  LOADING_ORGANISATION_LIST,
  LOADED_ORGANISATION,
  LOADING_ORGANISATION,
  EDIT_ORGANISATION,
  EDIT_ORGANISATION_SUCCESS,
  EDIT_ORGANISATION_ERROR,
  EDIT_ORGANISATION_ENGAGEMENT,
  EDIT_ORGANISATION_ENGAGEMENT_SUCCESS,
  EDIT_ORGANISATION_ENGAGEMENT_ERROR,
  ADD_ORGANISATION_ENGAGEMENT_EVENT,
  APPROVING_CHANGE,
  EDIT_ORGANISATION_ENGAGEMENT_EVENT,
  CLOSE_APPLICATION,
  REOPEN_APPLICATION,
  RELEASE_APPLICATION,
  SENDING_MESSAGE,
  SENDING_ATTACHMENTS,
  LOADED_APPLICATION,
  LOADING_APPLICATION,
  EDIT_APPLICATION,
  EDIT_APPLICATION_SUCCESS,
  EDIT_APPLICATION_ERROR,
  APPROVE_APPLICATION,
  REJECT_APPLICATION,
  WITHDRAW_APPLICATION,
  LOADED_EXPANSION_APPLICATION_LIST,
  LOADING_EXPANSION_APPLICATION_LIST,
  LOADED_ORGANISATION_SCOPES,
  LOADING_ORGANISATION_SCOPES,
  LOADED_APPLICATION_SCOPES,
  LOADING_APPLICATION_SCOPES,
  CREATE_SCOPES,
  ADD_SCOPE,
  TOGGLE_DELETE_SCOPE,
  DELETE_SCOPES,
  UPDATE_SCOPE,
  UPDATE_ORGS_PARAMS,
  ASSIGN_ORGANISATION,
  UNASSIGNING_ORGANISATION,
  UPDATING_SHARE,
  LOADED_SHARE
} from "./constants";

import { OrganisationEngagementEvent } from "../domain/OrganisationEngagementEvent";

import { CloseApplication } from "../domain/CloseApplication";
import { RecognitionApplication } from "../domain/RecognitionApplication";
import { ApproveApplication } from "../domain/ApproveApplication";
import { RejectApplication } from "../domain/RejectApplication";
import { ScopeApplicationSummary } from "../domain/ScopeApplication";
import { Scope } from "@ofqual/portal-organisations-scope/dist/module/domain/Scope";
import { LookupTrackerStore } from "@ofqual/portal-core/core/types";
import { ShareRecord } from "../domain/ShareRecord";
export interface ActionPayload<T> extends AnyAction {
  type: string;
  payload: T;
}

export const loadedOrganisationList = (
  payload: PagedResponse<Organisation>
) => ({
  type: LOADED_ORGANISATION_LIST,
  payload,
});

export const loadingOrganisationList = (payload: QueryParams) => ({
  type: LOADING_ORGANISATION_LIST,
  payload,
});

export const loadedOrganisation = (organisation: Organisation, share?: ShareRecord | null) => ({
  type: LOADED_ORGANISATION,
  payload: { organisation, share },
});

export const loadingOrganisation = (payload: string) => ({
  type: LOADING_ORGANISATION,
  payload,
});

export const updatingShare = (id: string, share: ShareRecord) => ({
  type: UPDATING_SHARE,
  payload: { id, share },
});

export const loadedShare = (share: ShareRecord | null) => ({
  type: LOADED_SHARE,
  payload: share,
});


export const editOrganisation = (
  itemId: number,
  document: JSONPatchDocument,
  meta: ChangeMeta[]
) => ({
  type: EDIT_ORGANISATION,
  payload: { itemId, document, meta },
});

export const editOrganisationSuccess = (payload: Organisation) => ({
  type: EDIT_ORGANISATION_SUCCESS,
  payload,
});

export const editOrganisationError = (payload: ValidationError | Error) => ({
  type: EDIT_ORGANISATION_ERROR,
  payload,
});

export const editOrganisationEngagement = (
  itemId: number,
  patchDoc: JSONPatchDocument
) => ({
  type: EDIT_ORGANISATION_ENGAGEMENT,
  payload: { itemId, patchDoc },
});

export const editOrganisationEngagementSuccess = (payload: Organisation) => ({
  type: EDIT_ORGANISATION_ENGAGEMENT_SUCCESS,
  payload,
});

export const editOrganisationEngagementError = (
  payload: ValidationError | Error
) => ({
  type: EDIT_ORGANISATION_ENGAGEMENT_ERROR,
  payload,
});

export const addOrganisationEngagementEvent = (
  orgId: number,
  newEvent: ModelWithMeta<OrganisationEngagementEvent>
) => ({
  type: ADD_ORGANISATION_ENGAGEMENT_EVENT,
  payload: { orgId, newEvent },
});

export const editOrganisationEngagementEvent = (
  orgId: number,
  updatedEvent: ModelWithMeta<OrganisationEngagementEvent>
) => ({
  type: EDIT_ORGANISATION_ENGAGEMENT_EVENT,
  payload: { orgId, updatedEvent },
});

export const approveRejectChange = (
  id: string,
  changeId: string,
  isApprove: boolean
) => ({
  type: APPROVING_CHANGE,
  payload: { id, changeId, isApprove },
});

export const closeApplication = (
  id: string,
  modelWithMeta: ModelWithMeta<CloseApplication>
) => ({
  type: CLOSE_APPLICATION,
  payload: { id, modelWithMeta },
});

export const reopenApplication = (id: string, notes: string) => ({
  type: REOPEN_APPLICATION,
  payload: { id, notes },
});

export const releaseApplication = (id: string) => ({
  type: RELEASE_APPLICATION,
  payload: { id },
});

export const sendMessage = (id: string, message: string) => ({
  type: SENDING_MESSAGE,
  payload: { id, message },
});

export const sendAttachments = (id: string, files: File[], note: string) => ({
  type: SENDING_ATTACHMENTS,
  payload: { id, files, note },
});

export const loadedApplication = (payload: RecognitionApplication) => ({
  type: LOADED_APPLICATION,
  payload,
});

export const loadingApplication = (orgId: string, applicationId: string) => ({
  type: LOADING_APPLICATION,
  payload: { orgId, applicationId },
});




export const editApplication = (
  orgId: string,
  applicationId: string,
  patchDocument: PatchWithMeta<JSONPatchDocument>,
  newReviewAttachments: File[],
  newPanelAttachments: File[],
  deletedReviewAttachmentIds: string[],
  deletedPanelAttachmentIds: string[]
) => ({
  type: EDIT_APPLICATION,
  payload: {
    orgId,
    applicationId,
    patchDocument,
    newReviewAttachments,
    newPanelAttachments,
    deletedReviewAttachmentIds,
    deletedPanelAttachmentIds,
  },
});

export const editApplicationSuccess = (payload: RecognitionApplication) => ({
  type: EDIT_APPLICATION_SUCCESS,
  payload,
});

export const editApplicationError = (payload: ValidationError | Error) => ({
  type: EDIT_APPLICATION_ERROR,
  payload,
});

export const approveApplication = (
  orgId: string,
  applicationId: string,
  approveApplication: ApproveApplication
) => ({
  type: APPROVE_APPLICATION,
  payload: { orgId, applicationId, approveApplication },
});

export const rejectApplication = (
  orgId: string,
  applicationId: string,
  rejectApplication: ModelWithMeta<RejectApplication>
) => ({
  type: REJECT_APPLICATION,
  payload: { orgId, applicationId, rejectApplication },
});

export const withdrawApplication = (orgId: string, applicationId: string) => ({
  type: WITHDRAW_APPLICATION,
  payload: { orgId, applicationId },
});

export const loadedExpansionApplicationList = (
  pagedResponse: PagedResponse<ScopeApplicationSummary>,
  itemId: string
) => ({
  type: LOADED_EXPANSION_APPLICATION_LIST,
  payload: { pagedResponse, itemId },
});

export const loadingExpansionApplicationList = (
  orgId: string,
  queryParams: QueryParams
) => ({
  type: LOADING_EXPANSION_APPLICATION_LIST,
  payload: { orgId, queryParams },
});

export const loadedOrganisationScopes = (payload: Scope[]) => ({
  type: LOADED_ORGANISATION_SCOPES,
  payload,
});

export const loadingOrganisationScopes = (payload: string) => ({
  type: LOADING_ORGANISATION_SCOPES,
  payload,
});

export const loadedApplicationScopes = (payload: Scope[]) => ({
  type: LOADED_APPLICATION_SCOPES,
  payload,
});

export const loadingApplicationScopes = (
  orgId: string,
  applicationId: string
) => ({
  type: LOADING_APPLICATION_SCOPES,
  payload: { orgId, applicationId },
});

export const createApplicationScopes = (
  orgId: string,
  applicationId: string,
  scopes: Scope[]
) => ({
  type: CREATE_SCOPES,
  payload: { orgId, applicationId, scopes },
});

export const addScope = (payload: Scope) => ({
  type: ADD_SCOPE,
  payload,
});

export const toggleDeleteScope = (scopes: Scope[]) => ({
  type: TOGGLE_DELETE_SCOPE,
  payload: {
    scopes,
  },
});

export const deleteSelectedScopes = () => ({
  type: DELETE_SCOPES,
});

export const updateScope = (
  updatedScope: Scope,
  lookupTrackingInfo: LookupTrackerStore
) => ({
  type: UPDATE_SCOPE,
  payload: {
    updatedScope,
    lookupTrackingInfo,
  },
});

export const updateOrganisationScopesParams = (newParams: QueryParams) => ({
  type: UPDATE_ORGS_PARAMS,
  payload: newParams,
});

export const assignItem = (
  organisationId: string,
  assignedToUpn: string,
  assignedToDisplayName: string
) => ({
  type: ASSIGN_ORGANISATION,
  payload: { organisationId, assignedToUpn, assignedToDisplayName },
});

export const unassignCase = (id: string) => ({
  type: UNASSIGNING_ORGANISATION,
  payload: { id },
});
